import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { HeartIcon, HandshakeIcon, DollarSignIcon, LightbulbIcon, UsersIcon, ClipboardCheckIcon, GlobeIcon, BookOpenIcon, AwardIcon, BriefcaseIcon, CheckCircleIcon, ArrowRightIcon, CreditCardIcon, BuildingIcon, StarIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export const SupportUsPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [donationAmount, setDonationAmount] = useState('100');
  const [donationFrequency, setDonationFrequency] = useState('monthly');
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const donationTiers = [{
    name: 'Supporter',
    amount: 'Monthly: $10-49',
    oneTime: 'One-time: $100-499',
    benefits: ['Recognition on our donor wall', 'Quarterly newsletter with research updates', 'Early access to educational content'],
    icon: <HeartIcon size={24} className="text-blue-600" data-id="element-1889" data-mp="3tpru14pwwpk" />
  }, {
    name: 'Advocate',
    amount: 'Monthly: $50-99',
    oneTime: 'One-time: $500-999',
    benefits: ['All Supporter benefits', 'Annual impact report', 'Invitation to virtual research presentations', 'Access to exclusive webinars'],
    icon: <BookOpenIcon size={24} className="text-blue-600" data-id="element-1890" data-mp="mrjxc56n0q37" />
  }, {
    name: 'Champion',
    amount: 'Monthly: $100-249',
    oneTime: 'One-time: $1,000-2,499',
    benefits: ['All Advocate benefits', 'Name recognition in annual publication', 'Quarterly virtual meet-and-greets with researchers', 'Personalized updates on research projects'],
    icon: <AwardIcon size={24} className="text-blue-600" data-id="element-1891" data-mp="2ygtc7ipt3mvs" />
  }, {
    name: 'Visionary',
    amount: 'Monthly: $250+',
    oneTime: 'One-time: $2,500+',
    benefits: ['All Champion benefits', 'Lab tour (virtual or in-person)', 'Recognition on major publications', 'Annual 1:1 consultation with research team', 'Input on future research priorities'],
    icon: <LightbulbIcon size={24} className="text-blue-600" data-id="element-1892" data-mp="20o3dnwaety52" />
  }];
  const partnershipOpportunities = [{
    title: 'Research Collaboration',
    description: 'Partner with our scientists on joint research initiatives investigating microbiome science and therapeutic applications.',
    icon: <ClipboardCheckIcon size={24} className="text-blue-600" data-id="element-1893" data-mp="1mn0vhumjd7zf" />
  }, {
    title: 'Corporate Sponsorship',
    description: 'Support specific research programs or educational initiatives with recognition and branding opportunities.',
    icon: <BuildingIcon size={24} className="text-blue-600" data-id="element-1894" data-mp="3gzh4h36pv07z" />
  }, {
    title: 'Educational Partnerships',
    description: 'Co-develop educational content, webinars, or workshops to advance understanding of microbiome science.',
    icon: <BookOpenIcon size={24} className="text-blue-600" data-id="element-1895" data-mp="3ok1jl1s8agqs" />
  }, {
    title: 'Technology Partners',
    description: 'Provide equipment, software, or technical expertise to enhance our research capabilities.',
    icon: <GlobeIcon size={24} className="text-blue-600" data-id="element-1896" data-mp="27uiv61zaprk" />
  }];
  const impactMetrics = [{
    metric: '25+',
    description: 'Research studies published',
    icon: <ClipboardCheckIcon size={24} className="text-blue-600" data-id="element-1897" data-mp="35n0c9ab7c4jo" />
  }, {
    metric: '10,000+',
    description: 'People educated through our resources',
    icon: <UsersIcon size={24} className="text-blue-600" data-id="element-1898" data-mp="3enf0ahfjvxqv" />
  }, {
    metric: '100+',
    description: 'Products tested for efficacy and safety',
    icon: <CheckCircleIcon size={24} className="text-blue-600" data-id="element-1899" data-mp="pxow3oq7mgcc" />
  }, {
    metric: '12',
    description: 'Countries where our research is implemented',
    icon: <GlobeIcon size={24} className="text-blue-600" data-id="element-1900" data-mp="sgahl55saebe" />
  }];
  const testimonials = [{
    quote: "Supporting the Ukweli Institute has been incredibly rewarding. Their commitment to scientific rigor in the wellness space is unmatched, and I'm proud to contribute to research that helps millions make better health decisions.",
    name: 'Dr. Michael Roberts',
    title: 'Monthly Donor since 2022',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
  }, {
    quote: "As a corporate partner, we've seen firsthand how the Institute's work elevates the entire supplement industry. Their research has helped us develop better products and build consumer trust through evidence-based claims.",
    name: 'Sarah Johnson',
    title: 'CEO, NutriWell Labs',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
  }, {
    quote: 'I volunteer my expertise because I believe in the mission. The Ukweli Institute is creating a much-needed bridge between consumer wellness products and rigorous scientific validation.',
    name: 'James Chen, PhD',
    title: 'Scientific Advisory Board Volunteer',
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
  }];
  const handleDonationAmountChange = e => {
    setDonationAmount(e.target.value);
  };
  return <div className="bg-white w-full" data-id="element-1901" data-mp="1h0x1fjery454">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50" data-id="element-1902" data-mp="1gykawhuyalgg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1903" data-mp="1l3a8cb59c83h">
          <div className="text-center" data-id="element-1904" data-mp="172hdtqeyhd0d">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl" data-id="element-1905" data-mp="173sdbe1dbylo">
              Support Our Mission
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-600" data-id="element-1906" data-mp="1c3n7bqgfx4ay">
              Help us advance evidence-based science in the wellness industry
              and empower consumers with trustworthy information
            </p>
          </div>
        </div>
      </section>
      {/* Why Support Section */}
      <section className="py-16 bg-white" data-id="element-1907" data-mp="34w1z1jhzjp6l">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1908" data-mp="300e4sponc5u8">
          <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={sectionVariants} transition={{
          duration: 0.8
        }} className="text-center mb-12" data-id="element-1909" data-mp="jy19dhne12n">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1910" data-mp="3ipt70ng2j107">
              Why Support the Ukweli Institute?
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto" data-id="element-1911" data-mp="2ca0mhqvv5lcu">
              The Ukweli Institute is creating a new standard for evidence-based
              wellness through independent research, education, and
              certification. Your support enables us to continue this critical
              work without commercial bias or influence.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mt-12" data-id="element-1912" data-mp="274db60fp0kx2">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-1913" data-mp="2bvd9na4gnb1o">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1914" data-mp="2ptjy5zniryoo">
                Your Support Enables Us To:
              </h3>
              <ul className="space-y-4" data-id="element-1915" data-mp="vhwsfi33adti">
                <li className="flex items-start" data-id="element-1916" data-mp="viy6zwjbdifz">
                  <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-3 mt-1" data-id="element-1917" data-mp="10g6tg9bhw8n1">
                    <CheckCircleIcon size={16} data-id="element-1918" data-mp="32cfl63frnfn2" />
                  </div>
                  <p className="text-gray-700" data-id="element-1919" data-mp="2xfp53g3yok1n">
                    <strong data-id="element-1920" data-mp="2xct6rqo7btco">Conduct Independent Research</strong> — Test
                    supplements, probiotics, and wellness products with
                    pharmaceutical-grade rigor, free from industry influence
                  </p>
                </li>
                <li className="flex items-start" data-id="element-1921" data-mp="2jg7bmnfq0ily">
                  <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-3 mt-1" data-id="element-1922" data-mp="2jgz6tx5eoebp">
                    <CheckCircleIcon size={16} data-id="element-1923" data-mp="2sgvr7mkrir0y" />
                  </div>
                  <p className="text-gray-700" data-id="element-1924" data-mp="2oeh76z5cxl83">
                    <strong data-id="element-1925" data-mp="2oco1pn6hd71o">Provide Unbiased Education</strong> — Create
                    evidence-based resources that help consumers and
                    practitioners navigate complex health decisions
                  </p>
                </li>
                <li className="flex items-start" data-id="element-1926" data-mp="b7sp8oky8uts">
                  <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-3 mt-1" data-id="element-1927" data-mp="b5y7fj4z5itx">
                    <CheckCircleIcon size={16} data-id="element-1928" data-mp="19d2exsyg7q3" />
                  </div>
                  <p className="text-gray-700" data-id="element-1929" data-mp="1bfzu2wft34u">
                    <strong data-id="element-1930" data-mp="67mvn2v4cy92">Develop Certification Standards</strong> — Establish
                    rigorous benchmarks that elevate the entire wellness
                    industry and protect consumers
                  </p>
                </li>
                <li className="flex items-start" data-id="element-1931" data-mp="ywc2liiuvxsq">
                  <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-3 mt-1" data-id="element-1932" data-mp="k8zb7d6daxuf">
                    <CheckCircleIcon size={16} data-id="element-1933" data-mp="p5hd85abci32" />
                  </div>
                  <p className="text-gray-700" data-id="element-1934" data-mp="1cvts603z7f35">
                    <strong data-id="element-1935" data-mp="1cv1umjqtj7a8">Build Global Research Networks</strong> —
                    Collaborate with scientists worldwide to advance microbiome
                    science and therapeutic applications
                  </p>
                </li>
              </ul>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-1936" data-mp="3foebi9sr2ttq">
              <img src="https://images.unsplash.com/photo-1581093458791-9a9cd7093ae3?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80" alt="Laboratory research at Ukweli Institute" className="rounded-lg shadow-xl w-full h-auto" onError={e => {
              e.currentTarget.src = 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80';
            }} data-id="element-1937" data-mp="2wtqw4ib3xdpy" />
            </motion.div>
          </div>
        </div>
      </section>
      {/* Ways to Support Section */}
      <section className="py-16 bg-gray-50" data-id="element-1938" data-mp="24d9d3l9i0uxk">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1939" data-mp="24ec2for6apbd">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-1940" data-mp="1zdwpuzeptlug">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1941" data-mp="2i57fld8pfskr">
              Ways to Support Our Mission
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-1942" data-mp="2x1f1chzrh225">
              There are many ways to contribute to the Ukweli Institute's work.
              Choose the option that best aligns with your interests and
              capacity.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1943" data-mp="344v8kr08bjem">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-white rounded-xl overflow-hidden shadow-md" data-id="element-1944" data-mp="343boepbgco7k">
              <div className="p-8" data-id="element-1945" data-mp="3rvihnflvsf2r">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6" data-id="element-1946" data-mp="oo44i9zpi4s">
                  <DollarSignIcon size={28} data-id="element-1947" data-mp="1xhrd2uiesle6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1948" data-mp="22g189544vlzs">
                  Financial Support
                </h3>
                <p className="text-gray-700 mb-6" data-id="element-1949" data-mp="1ux1mwtx43kds">
                  Make a one-time or recurring donation to fund our research and
                  educational initiatives. Every contribution, regardless of
                  size, helps advance our mission.
                </p>
                <a href="#donate" className="inline-flex items-center font-medium text-blue-600 hover:text-blue-800" data-id="element-1950" data-mp="1g7vtjmkygodt">
                  Donate now <ArrowRightIcon size={16} className="ml-1" data-id="element-1951" data-mp="2dt1jyj4y5e48" />
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-white rounded-xl overflow-hidden shadow-md" data-id="element-1952" data-mp="23wotdu4p7gdp">
              <div className="p-8" data-id="element-1953" data-mp="2cbb0lt80myu1">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6" data-id="element-1954" data-mp="2h444kevnu352">
                  <HandshakeIcon size={28} data-id="element-1955" data-mp="22gj5qx5tiq2y" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1956" data-mp="27d9jhdu5h9l7">
                  Partnerships
                </h3>
                <p className="text-gray-700 mb-6" data-id="element-1957" data-mp="2v4yfp5a3ei7f">
                  Collaborate with us through research partnerships, corporate
                  sponsorships, or educational initiatives that align with your
                  organization's values.
                </p>
                <a href="#partnerships" className="inline-flex items-center font-medium text-blue-600 hover:text-blue-800" data-id="element-1958" data-mp="2q40yauwwps6z">
                  Explore partnerships{' '}
                  <ArrowRightIcon size={16} className="ml-1" data-id="element-1959" data-mp="1eqghks50y49m" />
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="bg-white rounded-xl overflow-hidden shadow-md" data-id="element-1960" data-mp="3svbmxnxuck8f">
              <div className="p-8" data-id="element-1961" data-mp="358vcgem6r4vm">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6" data-id="element-1962" data-mp="355hcte4wp9rv">
                  <HeartIcon size={28} data-id="element-1963" data-mp="3e8icavz9o2mk" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1964" data-mp="3e65nbyv3cs00">
                  Volunteer
                </h3>
                <p className="text-gray-700 mb-6" data-id="element-1965" data-mp="2mh2k8mexxnoy">
                  Contribute your expertise, skills, or time to our research,
                  education, or outreach initiatives. We welcome professionals
                  from diverse backgrounds.
                </p>
                <a href="#volunteer" className="inline-flex items-center font-medium text-blue-600 hover:text-blue-800" data-id="element-1966" data-mp="udq7803wo7s7">
                  Get involved <ArrowRightIcon size={16} className="ml-1" data-id="element-1967" data-mp="144t7izp0giqf" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Donation Section */}
      <section id="donate" className="py-16 bg-white" data-id="element-1968" data-mp="bh71zlw8340x">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1969" data-mp="gf8m8a5dx9dz">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-1970" data-mp="ghuphxl8t2qe">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1971" data-mp="3hb98b669jhnq">
              Make a Donation
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-1972" data-mp="3cf2bc2vuhghx">
              Your financial support directly enables our independent research
              and educational initiatives. Choose a one-time or recurring
              donation.
            </p>
            <div className="mt-4" data-id="element-1973" data-mp="2xout9o9r1zhr">
              <Link to="/donor-recognition" className="inline-flex items-center font-medium text-blue-600 hover:text-blue-800" data-id="element-1974" data-mp="2xlyxo6ldjmwh">
                View our Donor Recognition Wall{' '}
                <ArrowRightIcon size={16} className="ml-1" data-id="element-1975" data-mp="2jok3ypo38syp" />
              </Link>
            </div>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-1976" data-mp="mwa1ddapud6x">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-gray-50 rounded-xl p-8" data-id="element-1977" data-mp="hwfd7hibs1pk">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1978" data-mp="3i57f75ek7u4b">
                Donation Form
              </h3>
              <div className="mb-6" data-id="element-1979" data-mp="344wmrxxlq7h1">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-1980" data-mp="395kmd7zwbwmv">
                  Select Donation Frequency
                </label>
                <div className="flex space-x-4" data-id="element-1981" data-mp="2u9czfzx3sh3w">
                  <button className={`flex-1 py-3 px-4 rounded-md font-medium ${donationFrequency === 'monthly' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300'}`} onClick={() => setDonationFrequency('monthly')} data-id="element-1982" data-mp="2uao2fxiq5kty">
                    Monthly
                  </button>
                  <button className={`flex-1 py-3 px-4 rounded-md font-medium ${donationFrequency === 'one-time' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300'}`} onClick={() => setDonationFrequency('one-time')} data-id="element-1983" data-mp="2hyu71oxk0dbx">
                    One-time
                  </button>
                </div>
              </div>
              <div className="mb-6" data-id="element-1984" data-mp="30n8tdywxf36e">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-1985" data-mp="30qekfjp48f4r">
                  Select Amount
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3" data-id="element-1986" data-mp="3an9ik1hh8dii">
                  {['25', '50', '100', '250'].map(amount => <button key={amount} className={`py-3 px-4 rounded-md font-medium ${donationAmount === amount ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300'}`} onClick={() => setDonationAmount(amount)} data-id="element-1987" data-mp="35o6eokd8gdc4">
                      ${amount}
                    </button>)}
                </div>
                <div className="relative" data-id="element-1988" data-mp="2yv110eqb6eoe">
                  <span className="absolute left-3 top-3 text-gray-500" data-id="element-1989" data-mp="3cn76n6vu539l">$</span>
                  <input type="number" className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Other amount" value={donationAmount} onChange={handleDonationAmountChange} data-id="element-1990" data-mp="37onjj35i9lfn" />
                </div>
              </div>
              <div className="mb-6" data-id="element-1991" data-mp="2oyxsmr4v5jt0">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-1992" data-mp="2oxway3tcdz2b">
                  Personal Information
                </label>
                <div className="space-y-3" data-id="element-1993" data-mp="99cwmasjj4tp">
                  <input type="text" className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Full Name" data-id="element-1994" data-mp="47dq4s1i7rsj" />
                  <input type="email" className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Email Address" data-id="element-1995" data-mp="1r327px8837a9" />
                </div>
              </div>
              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-md transition-colors duration-200 flex items-center justify-center" data-id="element-1996" data-mp="25w5o41a2veze">
                <CreditCardIcon size={18} className="mr-2" data-id="element-1997" data-mp="1w3q5d1qyimdg" />
                Proceed to Payment
              </button>
              <p className="text-sm text-gray-500 mt-4 text-center" data-id="element-1998" data-mp="21184y1j33p8s">
                Your donation is tax-deductible to the extent allowed by law.
                You will receive a receipt for your records.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-1999" data-mp="33fvrtlq27o8o">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2000" data-mp="2kgzwu86dn5ts">
                Donor Recognition Tiers
              </h3>
              <div className="space-y-6" data-id="element-2001" data-mp="2fognxoimwyvb">
                {donationTiers.map((tier, index) => <div key={tier.name} className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow duration-300" data-id="element-2002" data-mp="2argdl1er59ye" data-mpi={index}>
                    <div className="flex items-start" data-id="element-2003" data-mp="20uv8yh6na0v3">
                      <div className="flex-shrink-0 mr-4" data-id="element-2004" data-mp="1wqxcke8pf45q">
                        <div className="bg-blue-50 rounded-full p-3" data-id="element-2005" data-mp="c94wo28agslg">
                          {tier.icon}
                        </div>
                      </div>
                      <div data-id="element-2006" data-mp="g95aplrh71rz">
                        <h4 className="text-lg font-bold text-gray-900 mb-1" data-id="element-2007" data-mp="gcks4v8mgmwk">
                          {tier.name}
                        </h4>
                        <div className="flex flex-wrap gap-2 mb-3" data-id="element-2008" data-mp="q7uszfnaao8r">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2009" data-mp="3oqtcjg0zq7dn">
                            {tier.amount}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2010" data-mp="2j1tj97gdy9e">
                            {tier.oneTime}
                          </span>
                        </div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-2" data-id="element-2011" data-mp="35xxu7i48yrs4">
                          Benefits include:
                        </h5>
                        <ul className="space-y-1" data-id="element-2012" data-mp="1gi52i08ncoiy">
                          {tier.benefits.map((benefit, i) => <li key={i} className="flex items-start text-sm text-gray-600" data-id="element-2013" data-mp="1cime2uhm9vbd" data-mpi={i}>
                              <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-2014" data-mp="1lhranv9mbekw" />
                              <span data-id="element-2015" data-mp="1v6rf3u6w326h">{benefit}</span>
                            </li>)}
                        </ul>
                      </div>
                    </div>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Partnership Opportunities Section */}
      <section id="partnerships" className="py-16 bg-gray-50" data-id="element-2016" data-mp="9upy23u6hnae">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2017" data-mp="9wasltght689">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-2018" data-mp="2liusjmbhd8kz">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-2019" data-mp="22qshqrlismin">
              Partnership Opportunities
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-2020" data-mp="27r7ubgxz9pzk">
              Partner with the Ukweli Institute to advance microbiome science
              while aligning with your organization's goals and values.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2021" data-mp="3o4y14vghescw">
            {partnershipOpportunities.map((opportunity, index) => <motion.div key={opportunity.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white p-6 rounded-lg shadow-sm" data-id="element-2022" data-mp="3s58favefgp2u" data-mpi={index}>
                <div className="flex items-start" data-id="element-2023" data-mp="1nq4x48rdrzyh">
                  <div className="flex-shrink-0 mr-4" data-id="element-2024" data-mp="1jo067hsbhhfj">
                    <div className="bg-blue-50 rounded-full p-3" data-id="element-2025" data-mp="1ew0ay2n6zcpa">
                      {opportunity.icon}
                    </div>
                  </div>
                  <div data-id="element-2026" data-mp="19wdxbs3gbufy">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2027" data-mp="19v2vhxtkgwrh">
                      {opportunity.title}
                    </h3>
                    <p className="text-gray-700" data-id="element-2028" data-mp="22lb3wxyaw2sv">{opportunity.description}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="mt-12 text-center" data-id="element-2029" data-mp="2evkalxju8lky">
            <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-md hover:bg-blue-700 transition-colors duration-300" data-id="element-2030" data-mp="2xu4xteilz6tb">
              <HandshakeIcon size={18} className="mr-2" data-id="element-2031" data-mp="37j6d12hbxk68" />
              Discuss Partnership Opportunities
            </Link>
            <p className="mt-4 text-gray-600" data-id="element-2032" data-mp="3bnw7qo094spk">
              Contact us to discuss custom partnership arrangements that align
              with your organization's goals
            </p>
          </motion.div>
        </div>
      </section>
      {/* Volunteer Section */}
      <section id="volunteer" className="py-16 bg-white" data-id="element-2033" data-mp="qlxb4bfyd8v">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2034" data-mp="fowcl32uzxzq">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-2035" data-mp="fo4tgrqg7sqc">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-2036" data-mp="3sw7to4vax818">
              Volunteer Opportunities
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-2037" data-mp="3o2mvoctpke21">
              Contribute your expertise and skills to help advance our mission.
              We welcome volunteers from diverse backgrounds.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2038" data-mp="1axq8fp6q9c2t">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-gray-50 rounded-lg p-6" data-id="element-2039" data-mp="1aw6ui58jnd47">
              <div className="bg-blue-100 rounded-full p-3 inline-block mb-4" data-id="element-2040" data-mp="1ym9ia95fdwx0">
                <BriefcaseIcon size={24} className="text-blue-600" data-id="element-2041" data-mp="1koktiq88m5mo" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2042" data-mp="1oovb705e6khd">
                Professional Expertise
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-2043" data-mp="97uvvvhvqm4o">
                Volunteer your professional skills in areas such as:
              </p>
              <ul className="space-y-2 mb-6" data-id="element-2044" data-mp="d8547et59omp">
                <li className="flex items-start" data-id="element-2045" data-mp="38gobtszdhwx5">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2046" data-mp="38hzj42s11mt0" />
                  <span className="text-gray-700" data-id="element-2047" data-mp="3hhmkiokrasm2">
                    Scientific research and analysis
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2048" data-mp="3mda6kv3xz5nc">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2049" data-mp="2pqezhq9vgorq" />
                  <span className="text-gray-700" data-id="element-2050" data-mp="2yq22ggi922mw">
                    Medical and healthcare expertise
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2051" data-mp="33hk5da3u2dai">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2052" data-mp="261eay36wqh0w" />
                  <span className="text-gray-700" data-id="element-2053" data-mp="2aywo65ryjiep">
                    Legal and regulatory knowledge
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2054" data-mp="1yhvkjbvc3lia">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2055" data-mp="1yieua5atz3l8" />
                  <span className="text-gray-700" data-id="element-2056" data-mp="1phgy296mw0d7">
                    Marketing and communications
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2057" data-mp="1kjoxyrvu57xx">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2058" data-mp="1frnnnbi89raj" />
                  <span className="text-gray-700" data-id="element-2059" data-mp="1askilr28zl2k">IT and data science</span>
                </li>
              </ul>
              <Link to="/contact" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-2060" data-mp="15r4yq2bt3klr">
                Apply as a professional volunteer{' '}
                <ArrowRightIcon size={16} className="ml-1" data-id="element-2061" data-mp="n4xfv95dt4en" />
              </Link>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-gray-50 rounded-lg p-6" data-id="element-2062" data-mp="r698zongs7rl">
              <div className="bg-blue-100 rounded-full p-3 inline-block mb-4" data-id="element-2063" data-mp="1atyooxi4mhkg">
                <BookOpenIcon size={24} className="text-blue-600" data-id="element-2064" data-mp="w4svbq5yzlkh" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2065" data-mp="113x6gmcgftdg">
                Educational Initiatives
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-2066" data-mp="1osormtrmnbgq">
                Help us develop and deliver educational content:
              </p>
              <ul className="space-y-2 mb-6" data-id="element-2067" data-mp="1tmsup1xruzcj">
                <li className="flex items-start" data-id="element-2068" data-mp="3ead96yd9p2de">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2069" data-mp="3e927d43du4ox" />
                  <span className="text-gray-700" data-id="element-2070" data-mp="3n2f6wovyc4jz">
                    Content creation and editing
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2071" data-mp="2vcj1qorseyx4">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2072" data-mp="2vdbzx1cokg3n" />
                  <span className="text-gray-700" data-id="element-2073" data-mp="34d7dfctpkn6u">
                    Webinar and workshop facilitation
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2074" data-mp="2zckta53t6byo">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2075" data-mp="jmq5pwqslhrk" />
                  <span className="text-gray-700" data-id="element-2076" data-mp="endnxqes30vu">Curriculum development</span>
                </li>
                <li className="flex items-start" data-id="element-2077" data-mp="19zmwgglkas3i">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2078" data-mp="1ewmzsjr11gqx" />
                  <span className="text-gray-700" data-id="element-2079" data-mp="1juyaewp5bva7">Translation services</span>
                </li>
                <li className="flex items-start" data-id="element-2080" data-mp="1ownokl7o0p2i">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2081" data-mp="1sxhbpcda9zuc" />
                  <span className="text-gray-700" data-id="element-2082" data-mp="1xtxz24cnsa6d">Educational outreach</span>
                </li>
              </ul>
              <Link to="/contact" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-2083" data-mp="22p3ld49z3quz">
                Join our education team{' '}
                <ArrowRightIcon size={16} className="ml-1" data-id="element-2084" data-mp="2gm8wvjsghhxm" />
              </Link>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="bg-gray-50 rounded-lg p-6" data-id="element-2085" data-mp="4yx2wkjirpwq">
              <div className="bg-blue-100 rounded-full p-3 inline-block mb-4" data-id="element-2086" data-mp="omcsm5t4zsjw">
                <UsersIcon size={24} className="text-blue-600" data-id="element-2087" data-mp="jpe17v0uxzpe" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2088" data-mp="ewkz7ewrx5h0">
                Community Engagement
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-2089" data-mp="9xqf89n2lavb">
                Help us connect with communities and spread our mission:
              </p>
              <ul className="space-y-2 mb-6" data-id="element-2090" data-mp="12mxejaucm4hm">
                <li className="flex items-start" data-id="element-2091" data-mp="12m5gzuh6xwop">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2092" data-mp="2wurdg089f89y" />
                  <span className="text-gray-700" data-id="element-2093" data-mp="30vuugq21nacr">
                    Event organization and support
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2094" data-mp="2s42l6iora79r">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2095" data-mp="2s2htd0ttt2fl" />
                  <span className="text-gray-700" data-id="element-2096" data-mp="2i5mycrwrhk62">Community outreach</span>
                </li>
                <li className="flex items-start" data-id="element-2097" data-mp="246df1jd9e9xv">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2098" data-mp="247okroq9lrrm" />
                  <span className="text-gray-700" data-id="element-2099" data-mp="2e7eaodvewyg">Social media advocacy</span>
                </li>
                <li className="flex items-start" data-id="element-2100" data-mp="7dk3hfs7sg47">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2101" data-mp="7d27mk9gs3wt" />
                  <span className="text-gray-700" data-id="element-2102" data-mp="cj7a4sxfwsdn">Fundraising support</span>
                </li>
                <li className="flex items-start" data-id="element-2103" data-mp="qalx1augxdpz">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2104" data-mp="qbdgjnasjkzw" />
                  <span className="text-gray-700" data-id="element-2105" data-mp="107gtlo76l41m">Ambassador program</span>
                </li>
              </ul>
              <Link to="/contact" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-2106" data-mp="v83x0bqicilu">
                Become a community volunteer{' '}
                <ArrowRightIcon size={16} className="ml-1" data-id="element-2107" data-mp="157uridf2ixlx" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Impact Section */}
      <section className="py-16 bg-gray-50" data-id="element-2108" data-mp="36zrd6fuy9nuf">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2109" data-mp="370slickkzw8g">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-2110" data-mp="3viuz78amq9ru">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-2111" data-mp="3cltrhfpoxvk7">
              Our Impact
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-2112" data-mp="3hko7yb06r815">
              Your support enables real-world impact through research,
              education, and certification.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2113" data-mp="3qlur6ru7ywy3">
            {impactMetrics.map((item, index) => <motion.div key={item.description} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white rounded-lg p-8 text-center shadow-sm" data-id="element-2114" data-mp="15uc8f7aaf513" data-mpi={index}>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4" data-id="element-2115" data-mp="x9ch4uhktvfi">
                  {item.icon}
                </div>
                <div className="text-4xl font-bold text-blue-600 mb-2" data-id="element-2116" data-mp="n8trfj3bzkpo">
                  {item.metric}
                </div>
                <p className="text-gray-700" data-id="element-2117" data-mp="j9kxad4dpktl">{item.description}</p>
              </motion.div>)}
          </div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="mt-12 bg-white p-8 rounded-lg shadow-md" data-id="element-2118" data-mp="2hlih6umr92ep">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-2119" data-mp="2n6e593h5kr01">
              How Your Support Makes a Difference
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2120" data-mp="3ktd3vjreo90k">
              <div className="text-center" data-id="element-2121" data-mp="3fujotmwh09ya">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-600 mb-4" data-id="element-2122" data-mp="3fwwfqpk7hunh">
                  <DollarSignIcon size={24} data-id="element-2123" data-mp="3659v62lzaj82" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2124" data-mp="364qf6rfw24wo">
                  $10,000
                </h4>
                <p className="text-gray-700" data-id="element-2125" data-mp="2o9u3lqhv8tt">
                  Funds a comprehensive product testing program for safety and
                  efficacy
                </p>
              </div>
              <div className="text-center" data-id="element-2126" data-mp="aza5hc1cflo8">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-600 mb-4" data-id="element-2127" data-mp="exr6ddu67zx7">
                  <DollarSignIcon size={24} data-id="element-2128" data-mp="jw12bqnpz4jv" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2129" data-mp="owypyiri0qsr">
                  $25,000
                </h4>
                <p className="text-gray-700" data-id="element-2130" data-mp="tv06ox1gce12">
                  Enables a pilot clinical trial to validate therapeutic
                  approaches
                </p>
              </div>
              <div className="text-center" data-id="element-2131" data-mp="12p4wre8rhlfj">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-600 mb-4" data-id="element-2132" data-mp="12qfzdaqh0n52">
                  <DollarSignIcon size={24} data-id="element-2133" data-mp="1cmt371fqchdj" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2134" data-mp="1ckgbhwk66sna">
                  $50,000
                </h4>
                <p className="text-gray-700" data-id="element-2135" data-mp="rwyxienvzbhz">
                  Supports a year-long research program with multiple studies
                  and publications
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Testimonials Section */}
      <section className="py-16 bg-white" data-id="element-2136" data-mp="wwl9kkrzalp7">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2137" data-mp="wvjpjqsziovc">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-2138" data-mp="1an6u0dx2z3p7">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-2139" data-mp="3mczeq9smkyfg">
              From Our Supporters
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-2140" data-mp="3hexwts6xr55k">
              Hear from the individuals and organizations who support our
              mission.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2141" data-mp="21vhx93r8f7p1">
            {testimonials.map((testimonial, index) => <motion.div key={testimonial.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-gray-50 rounded-lg p-6 shadow-sm" data-id="element-2142" data-mp="1n5ss6xg0qjjh" data-mpi={index}>
                <div className="mb-6" data-id="element-2143" data-mp="2ylqu8hcs45b7">
                  <StarIcon size={20} className="text-yellow-500 inline-block" data-id="element-2144" data-mp="2ymsi5mew8mac" />
                  <StarIcon size={20} className="text-yellow-500 inline-block" data-id="element-2145" data-mp="33i7ug9x96a4n" />
                  <StarIcon size={20} className="text-yellow-500 inline-block" data-id="element-2146" data-mp="yizqc3qnm0ni" />
                  <StarIcon size={20} className="text-yellow-500 inline-block" data-id="element-2147" data-mp="uftbi7ph715f" />
                  <StarIcon size={20} className="text-yellow-500 inline-block" data-id="element-2148" data-mp="kj6rtm8vyg4z" />
                </div>
                <p className="text-gray-700 mb-6 italic" data-id="element-2149" data-mp="frfa817pjkqz">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center" data-id="element-2150" data-mp="arasy8yz6lzp">
                  <img src={testimonial.image} alt={testimonial.name} className="h-12 w-12 rounded-full object-cover mr-4" onError={e => {
                e.currentTarget.src = 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=250&q=80';
              }} data-id="element-2151" data-mp="6t3a98kybalv" />
                  <div data-id="element-2152" data-mp="2dif07o4y2f8f">
                    <h4 className="font-bold text-gray-900" data-id="element-2153" data-mp="2ifoqf605quxb">
                      {testimonial.name}
                    </h4>
                    <p className="text-sm text-gray-600" data-id="element-2154" data-mp="2mfz5d85xgvob">{testimonial.title}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-blue-50" data-id="element-2155" data-mp="1uq2q2f81u9nq">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2156" data-mp="1yqwfxe51xyj9">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-blue-600 rounded-xl p-8 md:p-12 text-center" data-id="element-2157" data-mp="1yplbdc3s8mr3">
            <h2 className="text-3xl font-bold text-white mb-6" data-id="element-2158" data-mp="29do4il0941cj">
              Join Us in Our Mission
            </h2>
            <p className="text-lg text-blue-100 max-w-3xl mx-auto mb-8" data-id="element-2159" data-mp="2o32envbnimsz">
              Together, we can transform the wellness industry with
              evidence-based science and empower consumers with trustworthy
              information.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2160" data-mp="1vfzllipqv4a4">
              <a href="#donate" className="px-6 py-3 bg-white text-blue-600 font-medium rounded-md shadow-md hover:bg-blue-50 transition-colors duration-300 flex items-center justify-center" data-id="element-2161" data-mp="1qgd8radtvq1u">
                <DollarSignIcon size={18} className="mr-2" data-id="element-2162" data-mp="3ftqdv1trli40" />
                Make a Donation
              </a>
              <Link to="/contact" className="px-6 py-3 bg-blue-700 text-white font-medium rounded-md shadow-md hover:bg-blue-800 transition-colors duration-300 flex items-center justify-center" data-id="element-2163" data-mp="3bsewtkoyi57f">
                <HandshakeIcon size={18} className="mr-2" data-id="element-2164" data-mp="3ocby01timorr" />
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
};